import {
    collection,
    doc,
    setDoc,
    updateDoc,
    deleteDoc,

    getDocs,
    query,
    where,
    onSnapshot,
    serverTimestamp,
    arrayUnion,
    arrayRemove,
    type Unsubscribe
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import type { LiveSession, UpdateLiveSessionData } from '@/types';
import { convertTimestamps, TIMESTAMP_FIELDS } from '@/utils/firestore';

export class LiveSessionService {
    private static readonly COLLECTION = 'liveSessions';

    /**
     * Start a new live session for a playlist
     */
    static async startSession(playlistId: string, adminId: string, playlistName: string, totalTracks: number): Promise<string> {
        // Check if session already exists for this playlist
        const existingSessions = await this.getSessionByPlaylist(playlistId);
        if (existingSessions) {
            return existingSessions.id; // Return existing session if found
        }

        const newSessionRef = doc(collection(db, this.COLLECTION));

        const sessionData: LiveSession = {
            id: newSessionRef.id,
            playlistId,
            adminId,
            isActive: true,
            currentVideoId: null, // Initially null
            currentVideoIndex: -1,
            isPlaying: false,
            currentTime: 0,
            volume: 100,
            isMuted: false,
            connectedUsers: [adminId],
            startedAt: new Date(), // Server timestamp will override
            lastUpdated: new Date(),
            metadata: {
                playlistName,
                adminName: '', // Should be passed or fetched, but for now empty
                totalTracks
            }
        };

        // We use serverTimestamp for dates, so cast to any for Firestore
        const firestoreData = {
            ...sessionData,
            startedAt: serverTimestamp(),
            lastUpdated: serverTimestamp()
        };

        await setDoc(newSessionRef, firestoreData);
        return newSessionRef.id;
    }

    /**
     * Get active session by playlist ID
     */
    static async getSessionByPlaylist(playlistId: string): Promise<LiveSession | null> {
        const q = query(
            collection(db, this.COLLECTION),
            where('playlistId', '==', playlistId),
            where('isActive', '==', true)
        );
        const snap = await getDocs(q);

        if (!snap.empty) {
            return convertTimestamps(snap.docs[0].data() as LiveSession, TIMESTAMP_FIELDS.liveSession as any);
        }
        return null;
    }

    /**
     * Get all active sessions
     */
    static async getActiveSessions(): Promise<LiveSession[]> {
        const q = query(
            collection(db, this.COLLECTION),
            where('isActive', '==', true)
        );
        const snap = await getDocs(q);

        return snap.docs.map(doc =>
            convertTimestamps(doc.data() as LiveSession, TIMESTAMP_FIELDS.liveSession as any)
        );
    }

    /**
     * Join an existing session
     */
    static async joinSession(sessionId: string, userId: string): Promise<void> {
        const sessionRef = doc(db, this.COLLECTION, sessionId);
        await updateDoc(sessionRef, {
            connectedUsers: arrayUnion(userId)
        });
    }

    /**
     * Leave a session
     */
    static async leaveSession(sessionId: string, userId: string): Promise<void> {
        const sessionRef = doc(db, this.COLLECTION, sessionId);
        await updateDoc(sessionRef, {
            connectedUsers: arrayRemove(userId)
        });
    }

    /**
     * Update session state (play, pause, seek, next track)
     * Only Admin should call this, verified by rules
     */
    static async updateSessionState(sessionId: string, updates: UpdateLiveSessionData): Promise<void> {
        const sessionRef = doc(db, this.COLLECTION, sessionId);
        await updateDoc(sessionRef, {
            ...updates,
            lastUpdated: serverTimestamp()
        });
    }

    /**
     * End a session (delete it or mark inactive)
     * For now, we'll mark it inactive? Or just delete it?
     * Spec says "Delete: Admin", so let's delete to keep it clean.
     */
    static async endSession(sessionId: string): Promise<void> {
        const sessionRef = doc(db, this.COLLECTION, sessionId);
        await deleteDoc(sessionRef);
    }

    /**
     * Subscribe to session updates
     */
    static subscribeToSession(sessionId: string, callback: (session: LiveSession | null) => void): Unsubscribe {
        const sessionRef = doc(db, this.COLLECTION, sessionId);

        return onSnapshot(sessionRef, (doc) => {
            if (doc.exists()) {
                const session = convertTimestamps(doc.data() as LiveSession, TIMESTAMP_FIELDS.liveSession as any);
                callback(session);
            } else {
                callback(null);
            }
        });
    }
}
